import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import BookAppointmentDialog from '../appointments/BookAppointmentDialog';
import WaitlistQueueEntryCard, {
  WaitlistQueueEntryActions,
  formatWaitlistPreference,
} from './WaitlistQueueEntryCard';
import WaitlistService from '../../services/waitlistService';
import TeamMemberService from '../../services/teamMemberService';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ManagementPageSkeleton from '../layout/ManagementPageSkeleton';

const EMPTY_SERVICE_IDS = [];

const WaitlistQueuePanel = ({ storeId, storeDetails }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const { hasPermission, isOwner } = usePermissions(storeId);
  const canManage = hasPermission(Permissions.AppointmentsCreate) || isOwner;

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [bookingEntry, setBookingEntry] = useState(null);
  const [removeEntry, setRemoveEntry] = useState(null);
  const [removing, setRemoving] = useState(false);

  const loadQueue = useCallback(async () => {
    if (!storeId) return;
    try {
      setLoading(true);
      const [queue, members] = await Promise.all([
        WaitlistService.getQueue(storeId),
        TeamMemberService.getTeamMembersByStore(storeId).catch(() => []),
      ]);
      setEntries(Array.isArray(queue) ? queue : []);
      setTeamMembers(Array.isArray(members) ? members : []);
    } catch (error) {
      console.error('Failed to load waitlist', error);
      showError(t('waitlistQueuePage.error.load'));
    } finally {
      setLoading(false);
    }
  }, [showError, storeId, t]);

  useEffect(() => {
    loadQueue();
  }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingCustomer = useMemo(
    () => (bookingEntry
      ? { customerId: bookingEntry.customerId, name: bookingEntry.customerName }
      : null),
    [bookingEntry],
  );

  const bookingSlotContext = useMemo(() => {
    const preference = bookingEntry?.preferences?.[0];
    if (!preference) return undefined;
    return {
      date: String(preference.date).slice(0, 10),
      startTime: String(preference.from).slice(0, 5),
    };
  }, [bookingEntry]);

  const handleRemove = async () => {
    if (!removeEntry) return;
    try {
      setRemoving(true);
      await WaitlistService.removeEntry(removeEntry.id, storeId);
      showSuccess(t('waitlistQueuePage.success.remove'));
      setRemoveEntry(null);
      await loadQueue();
    } catch (error) {
      console.error('Failed to remove waitlist entry', error);
      showError(t('waitlistQueuePage.error.remove'));
    } finally {
      setRemoving(false);
    }
  };

  const currency = storeDetails?.currency || DEFAULT_CURRENCY;

  const dialogs = (
    <>
      <BookAppointmentDialog
        open={Boolean(bookingEntry)}
        onClose={() => setBookingEntry(null)}
        storeId={storeId}
        slotContext={bookingSlotContext}
        teamMembers={teamMembers}
        storeDetails={storeDetails}
        storeLanguage={storeDetails?.supportedLanguages?.[0] || storeDetails?.storeLanguage}
        existingAppointments={[]}
        initialCustomer={bookingCustomer}
        initialServiceIds={bookingEntry?.serviceIds || EMPTY_SERVICE_IDS}
        onBooked={() => {
          setBookingEntry(null);
          loadQueue();
        }}
      />

      <ConfirmDialog
        open={Boolean(removeEntry)}
        onClose={() => setRemoveEntry(null)}
        onConfirm={handleRemove}
        title={t('waitlistQueuePage.removeTitle')}
        message={t('waitlistQueuePage.removeMessage', { name: removeEntry?.customerName })}
        confirmText={t('waitlistQueuePage.remove')}
        loading={removing}
        closeOnConfirm={false}
      />
    </>
  );

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('common.unauthorized')}</Alert>
      </Box>
    );
  }

  if (loading && entries.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <ManagementPageSkeleton />
        {dialogs}
      </Box>
    );
  }

  return (
    <Box>
      {entries.length === 0 ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">{t('waitlistQueuePage.empty')}</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 2 }}>
            {entries.map((entry) => (
              <WaitlistQueueEntryCard
                key={entry.id}
                entry={entry}
                currency={currency}
                onBook={() => setBookingEntry(entry)}
                onRemove={() => setRemoveEntry(entry)}
              />
            ))}
          </Box>

          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('waitlistQueuePage.columns.customer')}</TableCell>
                  <TableCell>{t('waitlistQueuePage.columns.services')}</TableCell>
                  <TableCell>{t('waitlistQueuePage.columns.preferences')}</TableCell>
                  <TableCell>{t('waitlistQueuePage.columns.joined')}</TableCell>
                  <TableCell>{t('waitlistQueuePage.columns.value')}</TableCell>
                  <TableCell align="right">{t('waitlistQueuePage.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.customerName}</TableCell>
                    <TableCell>{(entry.serviceNames || []).join(', ')}</TableCell>
                    <TableCell>
                      {(entry.preferences || []).map((preference) => (
                        <Typography key={`${preference.date}-${preference.from}`} variant="body2">
                          {formatWaitlistPreference(preference)}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>{formatUtcDate(entry.createdAtUtc)}</TableCell>
                    <TableCell>{formatCurrency(entry.estimatedAmountDue, currency)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        position: 'sticky',
                        right: 0,
                        bgcolor: 'rgba(255,255,255,0.97)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <WaitlistQueueEntryActions
                        onBook={() => setBookingEntry(entry)}
                        onRemove={() => setRemoveEntry(entry)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {dialogs}
    </Box>
  );
};

export default WaitlistQueuePanel;
